/**
 * Binance WebSocket Service
 * Handles WebSocket connections to Binance for live crypto prices
 */

export class BinanceWebSocket {
    constructor() {
        this.subscribers = new Map();
        this.ws = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    connect() {
        // Use the combined stream endpoint
        this.ws = new WebSocket('wss://stream.binance.com:9443/stream');

        this.ws.onopen = () => {
            console.log('Binance WebSocket Connected');
            this.reconnectAttempts = 0;
            this.subscribe();
        };

        this.ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.stream && data.data) {
                const symbol = data.stream.split('@')[0].toUpperCase();
                const callback = this.subscribers.get(symbol);
                if (callback) {
                    callback({
                        symbol,
                        lastPrice: parseFloat(data.data.c),
                        priceChange: parseFloat(data.data.p),
                        priceChangePercent: parseFloat(data.data.P),
                        high: parseFloat(data.data.h),
                        low: parseFloat(data.data.l),
                        volume: parseFloat(data.data.v),
                        quoteVolume: parseFloat(data.data.q)
                    });
                }
            }
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        this.ws.onclose = () => {
            console.log('WebSocket Connection Closed');
            this.reconnect();
        };
    }

    subscribe() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.subscribers.size > 0) {
            const symbols = Array.from(this.subscribers.keys());
            const subscribeMsg = {
                method: 'SUBSCRIBE',
                params: symbols.map(s => `${s.toLowerCase()}@ticker`),
                id: Date.now()
            };
            this.ws.send(JSON.stringify(subscribeMsg));
        }
    }

    addSymbol(symbol, callback) {
        if (!this.subscribers.has(symbol)) {
            this.subscribers.set(symbol, callback);
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                const subscribeMsg = {
                    method: 'SUBSCRIBE',
                    params: [`${symbol.toLowerCase()}@ticker`],
                    id: Date.now()
                };
                this.ws.send(JSON.stringify(subscribeMsg));
            } else if (!this.ws) {
                this.connect();
            }
        }
    }

    removeSymbol(symbol) {
        if (this.subscribers.has(symbol) && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const unsubscribeMsg = {
                method: 'UNSUBSCRIBE',
                params: [`${symbol.toLowerCase()}@ticker`],
                id: Date.now()
            };
            this.ws.send(JSON.stringify(unsubscribeMsg));
            this.subscribers.delete(symbol);
        }
    }

    reconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            setTimeout(() => this.connect(), 5000);
        }
    }

    close() {
        if (this.ws) {
            this.ws.close();
            this.subscribers.clear();
        }
    }
}

// Create a singleton instance
const binanceWS = new BinanceWebSocket();
export default binanceWS;
